import React, { useEffect, useId, useRef } from 'react';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Rendered right-aligned in the footer band. Omit for a dialog with no actions. */
  footer?: React.ReactNode;
}

/**
 * Modal dialog primitive, replacing the Ant Design `Modal` on Bank Info.
 *
 * Renders nothing at all when closed, so a closed dialog leaves no DOM and no
 * listeners behind.
 */
const Dialog: React.FC<DialogProps> = ({ open, onClose, title, children, footer }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  /**
   * `onClose` is nearly always an inline arrow at the call site, so its
   * identity changes every render. Reading it through a ref keeps it out of
   * the effect's dependency array — with `onClose` listed there, the effect
   * would tear down and re-run on every render, and the `panel.focus()` below
   * would yank the caret out of whichever field the user was typing in.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panel?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;

      // Keep Tab inside the dialog. Without this, tabbing past the last
      // control moves focus to the page behind the backdrop, where it is
      // invisible but still typeable.
      const focusable = panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/*
        The backdrop is its own element rather than a click handler on the
        wrapper. On the wrapper, a drag that starts inside a text field and
        releases outside the panel would register as a backdrop click and
        discard the user's edits.
      */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-brand-900/50 animate-fade-in motion-reduce:animate-none"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={
          'relative z-10 w-full max-w-md overflow-hidden rounded-lg bg-white ' +
          'shadow-[0_24px_60px_-20px_rgba(42,38,38,.45)] outline-none ' +
          'animate-dialog-in motion-reduce:animate-none'
        }
      >
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px"
          style={{
            backgroundImage:
              'linear-gradient(90deg, rgba(200,149,47,0) 0%, rgba(200,149,47,.9) 50%, rgba(200,149,47,0) 100%)',
          }}
        />

        <div className="border-0 border-b border-solid border-brand-300/70 px-6 py-5">
          <h2 id={titleId} className="m-0 font-display text-xl font-normal text-brand-900">
            {title}
          </h2>
        </div>

        <div className="px-6 py-6">{children}</div>

        {footer && (
          <div className="flex justify-end gap-3 border-0 border-t border-solid border-brand-300/70 bg-brand-50 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialog;
